import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Search, Filter as FilterIcon } from 'lucide-react';
import { MODULE_TABS, MODULE_NUMBER_BY_KEY } from '../../shared/moduleTabs';
import { PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize, Pagination } from '../../shared/pagination';
import { useErrorToast, useDismissError } from '../../shared/ErrorToastContext';
import SavedFilterTabs from '../../shared/SavedFilterTabs';
import FilterDrawer from '../../shared/FilterDrawer';
import CancelButton from '../../shared/CancelButton';
import { CategoryPill } from '../../shared/Pill';
import '../../shared/ListRowHover.css';
import '../../shared/StyledInput.css';

// Numbers come from MODULE_NUMBER_BY_KEY (the actual assigned module
// number), not MODULE_TABS' array position — the two stopped matching once
// the old "Customer" module (number 2) was removed and its number
// permanently retired. See moduleTabs.js.
const MODULE_LEGEND = MODULE_TABS.map((module) => `${MODULE_NUMBER_BY_KEY[module.key]} ${module.label}`).join(' · ');
// Same mapping, keyed for quick per-row lookup (Module column) and for
// building the filter's own option list.
const MODULE_LABEL_BY_NUMBER = Object.fromEntries(MODULE_TABS.map((module) => [MODULE_NUMBER_BY_KEY[module.key], module.label]));

// Same SavedFilterTabs pattern as Products/Price Adjustments — a quick-
// select for ad hoc narrowing, plus the drawer for saved tabs, both bound
// to the same filters object. Default is 'all' (not 'shown') since this
// list existed with no filter at all until now — defaulting to Shown-only
// would hide rows an admin could previously always see.
const FILTER_SCREEN_KEY = 'screen-help-settings';
const DEFAULT_FILTERS = { status: 'all', module: '' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { width: '100%' };

// react-select can't take className="styled-input" directly (see
// ui-architecture.md's Icon Search Box & Local-List Select) — pin the same
// light-grey-border/blue-focus look here instead.
const ARTICLE_SELECT_STYLES = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    height: 30,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : '#a9a9a9',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
    fontSize: 13,
    '&:hover': { borderColor: state.isFocused ? '#1976d2' : '#a9a9a9' },
  }),
  valueContainer: (base) => ({ ...base, height: 28, padding: '0 6px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 28 }),
  option: (base) => ({ ...base, fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

function formatCode(row) {
  return row.module_number != null && row.sequence != null ? `${row.module_number}-${row.sequence}` : '—';
}

// Mirrors EXPLICIT_ROUTES' one special case in App.js — every other
// pageKey routes at `/${pageKey}` verbatim, dashboard alone lives at '/'.
function screenPath(pageKey) {
  return pageKey === 'dashboard' ? '/' : `/${pageKey}`;
}

function ScreenSettingsTable() {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });
  const [articleOptions, setArticleOptions] = useState([]);
  const articleSelectOptions = useMemo(
    () => articleOptions.map((article) => ({ value: article.slug, label: `${article.group} / ${article.title}` })),
    [articleOptions],
  );
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { status, module } = filters;
  const applyFilters = (values) => { setFilters(values); setPage(1); };

  // Ad hoc "Filters" panel — same shape as ProductTable.js: draft is null
  // while closed, so opening always seeds a fresh copy of the live filters.
  const [filterDraft, setFilterDraft] = useState(null);
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/manual-articles')
      .then((res) => setArticleOptions(res.data.data || []))
      .catch(() => setArticleOptions([]));
  }, []);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/manual-screens', { params: { search, status, module, page, limit } });
      setRows(res.data.data || []);
      setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
    } catch (err) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line
  }, [search, status, module, page, limit]);

  const patchRow = async (row, patch, revert) => {
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...patch } : r)));
    try {
      await axios.patch(`/api/manual-screens/${row.id}`, patch);
      dismissError();
    } catch (err) {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...revert } : r)));
      showError(err, 'Could not update this screen');
    }
  };

  const toggle = (row) => patchRow(row, { enabled: !row.enabled }, { enabled: row.enabled });
  const setArticle = (row, article_slug) => patchRow(row, { article_slug: article_slug || null }, { article_slug: row.article_slug });

  // Shared by both SavedFilterTabs (save-as-a-tab) and the ad hoc Filters
  // panel below (apply-now) — same field-render-function split as
  // ProductTable.js, so the two never drift apart.
  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>Status</label><br />
        <select className="styled-input" value={draft.status} onChange={(e) => setDraftField('status', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All screens</option>
          <option value="shown">Shown</option>
          <option value="hidden">Hidden</option>
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Module</label><br />
        <select className="styled-input" value={draft.module} onChange={(e) => setDraftField('module', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="">All modules</option>
          {MODULE_TABS.map((m) => (
            <option key={m.key} value={MODULE_NUMBER_BY_KEY[m.key]}>{MODULE_NUMBER_BY_KEY[m.key]} {m.label}</option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <h2 style={{ padding: '20px 24px 4px', margin: 0, fontWeight: 600, fontSize: 20 }}>Screen Help Settings</h2>
      <p style={{ padding: '0 24px', margin: '0 0 12px', color: '#888', fontSize: 12 }}>
        Code = module — screen, e.g. 2-15. Module numbers: {MODULE_LEGEND}.
      </p>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '0 24px 14px', flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            background: '#fff',
            border: '1px solid ' + (searchFocused ? '#1976d2' : '#d0d5dd'),
            boxShadow: searchFocused ? '0 0 0 2px rgba(25, 118, 210, 0.15)' : 'none',
            borderRadius: 8,
            maxWidth: 280,
          }}
        >
          <Search size={14} strokeWidth={1.75} color="#888" />
          <input
            type="text"
            placeholder="Search screens..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={{ border: 'none', outline: 'none', fontSize: 14, width: '100%' }}
          />
        </div>
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 6,
            border: '1px solid #bfc8d6',
            background: activeFilterCount ? '#eef1f5' : '#fff',
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div style={{ margin: '0 24px 14px' }}>
        <SavedFilterTabs
          screenKey={FILTER_SCREEN_KEY}
          defaultFilters={DEFAULT_FILTERS}
          currentFilters={filters}
          onApply={applyFilters}
          renderFields={renderDrawerFields}
        />
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the screen list."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Apply
                </button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '0 24px', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'visible', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '9%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '29%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '22%' }} />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Code</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Module</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Screen</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Article</th>
              <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'center' }}>Show Help</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No screens match.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.id} className="list-row">
                <td style={{ padding: '6px 8px', fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatCode(row)}</td>
                <td style={{ padding: '6px 8px' }}>
                  {MODULE_LABEL_BY_NUMBER[row.module_number] ? <CategoryPill value={MODULE_LABEL_BY_NUMBER[row.module_number]} /> : '—'}
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <Link to={screenPath(row.page_key)} style={{ color: '#1976d2', fontSize: 14, textDecoration: 'none' }}>
                    {row.label}
                  </Link>
                </td>
                <td style={{ padding: '6px 8px' }}>
                  <Select
                    value={articleSelectOptions.find((opt) => opt.value === row.article_slug) || null}
                    onChange={(opt) => setArticle(row, opt ? opt.value : '')}
                    options={articleSelectOptions}
                    placeholder="— none —"
                    isClearable
                    isSearchable
                    styles={ARTICLE_SELECT_STYLES}
                    menuPortalTarget={document.body}
                  />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={() => toggle(row)}
                    style={{
                      padding: '5px 14px',
                      borderRadius: 999,
                      border: '1px solid ' + (row.enabled ? '#93c5fd' : '#d0d5dd'),
                      background: row.enabled ? '#dbeafe' : '#fff',
                      color: row.enabled ? '#1e40af' : '#667085',
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      minWidth: 68,
                    }}
                  >
                    {row.enabled ? 'Shown' : 'Hidden'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14 }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default ScreenSettingsTable;
